import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { LogIn, Building2, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { API } from '../App';


export default function PartnerLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check if user arrived via invite link
  const inviteToken = searchParams.get('token');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/partner/login`, { email, password });
      const { access_token, partner } = response.data;
      
      localStorage.setItem('partner_token', access_token);
      localStorage.setItem('partner_data', JSON.stringify(partner));
      
      toast.success('Login successful!');
      navigate('/partner');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // If invite token is present, redirect to registration
  if (inviteToken) {
    navigate(`/partner/register?token=${inviteToken}`);
    return null;
  }

  return (
    <div className="min-h-screen flex relative">
      <div className="flex-1 flex items-center justify-center p-8 bg-blue-50">
        <div className="w-full max-w-md">
          
          <div className="mb-8 flex flex-col items-center">
            <img 
              src="/logo.jpg" 
              alt="Outsiders Travel" 
              className="h-32 w-auto object-contain mb-4"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <h1 className="text-3xl font-bold text-black mb-2" data-testid="partner-login-title">
              Partner Portal
            </h1>
            <p className="text-gray-600 text-lg text-center">
              Outsiders Travel Partner Management
            </p>
          </div>

          <div className="bg-white border border-blue-200 rounded-sm shadow-sm p-8">
            <h2 className="text-2xl font-semibold mb-6 text-black flex items-center gap-2" data-testid="partner-login-form-title">
              <UserCheck className="h-6 w-6 text-sky-500" />
              Partner Login
            </h2>
            
            <form onSubmit={handleLogin} className="space-y-5" data-testid="partner-login-form">
              <div>
                <Label htmlFor="email" className="text-gray-700 font-medium mb-2 block">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="partner@company.com"
                  required
                  className="h-11 rounded-sm border-blue-200 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  data-testid="partner-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-gray-700 font-medium mb-2 block">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="h-11 rounded-sm border-blue-200 focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  data-testid="partner-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-sky-500 hover:bg-sky-600 text-white font-medium rounded-sm transition-all"
                data-testid="partner-login-button"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Logging in...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <LogIn className="mr-2 h-5 w-5" />
                    Sign In
                  </span>
                )}
              </Button>
              
              <div className="text-center">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-sky-500 hover:text-sky-600"
                  data-testid="partner-forgot-password-link"
                >
                  Forgot your password?
                </Link>
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Not a partner?{' '}
                <Link to="/login" className="text-sky-500 hover:text-sky-600 font-medium">
                  Employee Login
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        className="hidden lg:flex flex-1 items-center justify-center p-12 relative overflow-hidden bg-black"
      >
        <div className="relative z-10 text-white max-w-lg">
          <h2 className="text-4xl font-bold mb-4">Partner Portal</h2>
          <p className="text-blue-200 text-lg leading-relaxed mb-6">
            Manage your rides, track payments, and upload invoices all in one place.
          </p>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-sky-500 flex items-center justify-center">
                <span className="text-sm font-bold">1</span>
              </div>
              <span>View all your assigned rides</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-sky-500 flex items-center justify-center">
                <span className="text-sm font-bold">2</span>
              </div>
              <span>Track payment status</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-sky-500 flex items-center justify-center">
                <span className="text-sm font-bold">3</span>
              </div>
              <span>Upload invoices for completed work</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
